import React, { useMemo, useState } from 'react'
import { Formik } from 'formik'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'

import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import usePostingNoticeRequestContext from 'hooks/posting_notice_request/usePostingNoticeRequestContext'

import FooterButtons from './FooterButtons'
import useNotification from 'hooks/context/useNotification'

import ConfirmationModal from './ConfirmationModal'
import saveData from 'helpers/browser/saveData'
import useConfiguration from 'hooks/useConfiguration'
import { DELIVERY_TYPES } from 'utils/constants'

function Row(props) {
  const { row } = props
  const [open, setOpen] = useState(true)

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          <Typography variant="h6">{row.name}</Typography>
        </TableCell>
      </TableRow>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small" aria-label="row children">
                <TableBody>
                  {row.children.map((childRow) => (
                    <TableRow key={childRow.name}>
                      <TableCell sx={{ width: '50%' }} colSpan={6}>
                        <Typography fontWeight={500} fontSize={14}>
                          {childRow.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '50%' }} colSpan={6}>
                        {childRow.value}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

function Review() {
  const {
    shippingAddress,
    generalInformation,
    postingNotices,
    coverLetter,
    sortingOption,
    generalInformationRenderingData,
    generalInformationAddressMapping,
    reset,
    createPostingNoticeRequest,
    submitPostingNoticeRequest,
    downloadPostingNoticeRequest,
    mailType,
  } = usePostingNoticeRequestContext()
  const { setBasicNotification, setError } = useNotification()
  const [postingNoticeRequest, setPostingNoticeRequest] = useState(null)
  const [hasModalError, setHasModalError] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const { canAccessModule } = useConfiguration()
  const canAccessDocApproval = canAccessModule(
    '/command-center/document-approval'
  )

  const getFieldDisplay = (field, value) => {
    if (field.type === 'date') {
      return format(new Date(value), 'MM/dd/yyyy')
    }

    if (field.type === 'address') {
      if (!value) {
        return 'N/A'
      }

      const { address, city, state, zip } =
        generalInformationAddressMapping[field.name]

      return (
        <Box>
          <Box>{generalInformation[address.key]}</Box>
          <Box>
            {generalInformation[city.key]}, {generalInformation[state.key]}{' '}
            {generalInformation[zip.key]}
          </Box>
        </Box>
      )
    }

    return value?.toString() || 'N/A'
  }

  const handleSubmit = async (postingNoticeRequestId) =>
    submitPostingNoticeRequest(postingNoticeRequestId).then((res) => {
      const letters = Array.isArray(res.PostingNoticeRequestLetterIDs)
        ? res.PostingNoticeRequestLetterIDs
        : []
      const plural = letters.length > 1

      if (!letters.length && !isEmailDelivery) {
        setError(
          'Unable to submit posting notice request. Please try again.',
          res
        )
        setPostingNoticeRequest(null)
        return
      }

      setBasicNotification(
        <>
          Thank you for submitting the posting notice request.{' '}
          {isEmailDelivery && <> The email will be queued and sent shortly.</>}
          {isMailDelivery && (
            <>
              <Box>
                The letter{plural && 's'} that will be sent{' '}
                {plural ? 'have' : 'has'} the id
                {plural && 's'} of{' '}
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  {letters.join(', ')}
                </Box>
                .
              </Box>
              {canAccessDocApproval && (
                <Box>
                  Check{' '}
                  <Link to="/command-center/document-approval">
                    Doc Approval
                  </Link>
                  .
                </Box>
              )}
            </>
          )}
        </>
      )
      reset()
    })

  const rows = useMemo(() => {
    const _rows = []

    const shippingAddressRow = {
      name: 'Delivery Address',
      children: [],
    }

    shippingAddressRow.children.push({
      name: 'Shipping Method',
      value: shippingAddress.email ? 'Email' : 'Mail',
    })

    if (shippingAddress.email) {
      shippingAddressRow.children.push({
        name: 'Shipping Address',
        value: `${shippingAddress.name} (${shippingAddress.email})`,
      })
    } else {
      shippingAddressRow.children.push({
        name: 'Shipping Address',
        value: (
          <Box>
            <Box>{shippingAddress.company_name}</Box>
            <Box>{shippingAddress.name}</Box>
            <Box>{shippingAddress.address_line_1}</Box>
            <Box>{shippingAddress.address_line_2}</Box>
            <Box>{shippingAddress.address_line_3}</Box>
            <Box>{shippingAddress.address_line_4}</Box>
            <Box>
              {shippingAddress.city}, {shippingAddress.state}{' '}
              {shippingAddress.zip_code}
            </Box>
            <Box>{shippingAddress.country}</Box>
          </Box>
        ),
      })
    }

    _rows.push(shippingAddressRow)

    generalInformationRenderingData.forEach((section) => {
      _rows.push({
        name: section.title,
        children: section.fields.map((field) => ({
          name: field.label,
          value: getFieldDisplay(field, generalInformation[field.name]),
        })),
      })
    })

    _rows.push({
      name: 'Posting Notices',
      children: postingNotices.map((notice) => ({
        name: `State: ${notice.state}`,
        value: `Quantity: ${notice.quantity}`,
      })),
    })

    _rows.push({
      name: 'Cover Letter and Attachments',
      children: [
        {
          name: 'Cover Letter',
          value: coverLetter.coverLetter?.name || 'N/A',
        },
        ...(coverLetter.attachments.length === 0
          ? [
              {
                name: 'Attachments',
                value: 'N/A',
              },
            ]
          : coverLetter.attachments.map((attachment, index) => ({
              name: `Attachment #${index + 1}`,
              value: attachment.name,
            }))),
      ],
    })

    _rows.push({
      name: 'Sorting Option',
      children: [
        {
          name: 'Sorting Option',
          value: sortingOption,
        },
      ],
    })

    return _rows
  }, [
    shippingAddress,
    generalInformation,
    postingNotices,
    coverLetter,
    sortingOption,
  ])

  const isMailDelivery = mailType === DELIVERY_TYPES.MAIL
  const isEmailDelivery = mailType === DELIVERY_TYPES.EMAIL

  return (
    <>
      <ConfirmationModal
        open={showModal && Boolean(postingNoticeRequest)}
        hasError={hasModalError}
        onClose={() => setShowModal(false)}
        postingNoticeRequest={postingNoticeRequest}
        isMail={isMailDelivery}
        isEmail={isEmailDelivery}
        onSubmit={async () => {
          try {
            await handleSubmit(postingNoticeRequest?.PostingNoticeRequestID)
          } catch (error) {
            setError(
              error?.response?.data?.message ||
                'Unable to submit posting notice request. Please try again.',
              error
            )
          }
        }}
        onDownload={async () => {
          try {
            const pdf = await downloadPostingNoticeRequest(
              postingNoticeRequest?.PostingNoticeRequestID
            )

            saveData(
              pdf,
              `PostingNoticeRequest_${postingNoticeRequest?.PostingNoticeRequestPubID}.pdf`,
              'application/pdf'
            )

            setBasicNotification(
              'A PDF file has been automatically downloaded to your computer.'
            )
            reset()
          } catch (error) {
            setError(
              error?.response?.data?.message ||
                'Unable to download posting notice request. Please try again.',
              error
            )
          }
        }}
      />
      <Formik
        initialValues={{}}
        onSubmit={async () => {
          let _postingNoticeRequest = postingNoticeRequest

          try {
            if (!_postingNoticeRequest) {
              _postingNoticeRequest = await createPostingNoticeRequest()
              setPostingNoticeRequest(_postingNoticeRequest)
            }

            if (isEmailDelivery) {
              // Send email immediately w/o confirmation
              await handleSubmit(
                _postingNoticeRequest.PostingNoticeRequestID
              ).catch(() => {
                setHasModalError(true)
                setShowModal(true)
              })
            } else {
              setShowModal(true)
            }
          } catch (error) {
            setError(
              error?.response?.data?.message ||
                'Unable to submit posting notice request. Please try again.',
              error
            )
          }
        }}
      >
        <TableContainer component={Paper}>
          <Table aria-label="collapsible table">
            <TableBody>
              {rows.map((row) => (
                <Row key={row.name} row={row} />
              ))}
            </TableBody>
          </Table>
          <Box pr={4} pb={8}>
            <FooterButtons />
          </Box>
        </TableContainer>
      </Formik>
    </>
  )
}

export default Review
